/**
 * Minimal staff-ui-local Repeater primitive.
 *
 * Mirrors the canonical account-ui Repeater used by the team-page form, but
 * uses design-system `Button` + the shared `ProfilesForm.module.css` so the
 * row chrome respects light/dark theme tokens.
 */
import { type ReactNode } from "react";
import { Button } from "@legal-500/legal500-design-system";
import styles from "../../pages/Profiles/ProfilesForm.module.css";

export type RepeaterProps<T> = {
  items: T[];
  onChange: (next: T[]) => void;
  /**
   * Render one row. The 4th `errs` arg is the per-row sub-field error map for
   * row `index` (e.g. `{ title: "Required" }`), sourced from `rowErrors` — pass
   * `errs?.<sub>` to the matching input's `error` prop. Mirrors the account-ui
   * Repeater signature so forms can show inline sub-field validation.
   */
  renderRow: (
    item: T,
    index: number,
    update: (patch: Partial<T>) => void,
    errs?: Record<string, string>,
  ) => ReactNode;
  createEmpty: () => T;
  addLabel: string;
  emptyState?: ReactNode;
  removeLabel?: string;
  /**
   * Optional per-row error maps, indexed by row position (sparse). Entry `i`
   * holds the sub-field errors for row `i`. Wires straight from
   * `useFormValidation`'s `arrayErrors.<field>`.
   */
  rowErrors?: Array<Record<string, string> | undefined>;
};

export function Repeater<T>({
  items,
  onChange,
  renderRow,
  createEmpty,
  addLabel,
  emptyState,
  removeLabel = "Remove item",
  rowErrors,
}: RepeaterProps<T>) {
  const handleAdd = () => onChange([...items, createEmpty()]);
  const handleRemove = (index: number) => onChange(items.filter((_, i) => i !== index));
  const handleUpdate = (index: number, patch: Partial<T>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <div>
      {items.length === 0 && emptyState && <p className={styles.repeaterEmpty}>{emptyState}</p>}
      {items.length > 0 && (
        <ul className={styles.repeaterList}>
          {items.map((item, index) => (
            <li key={index} className={styles.repeaterItem}>
              {renderRow(
                item,
                index,
                (patch: Partial<T>) => handleUpdate(index, patch),
                rowErrors?.[index],
              )}
              <div className={styles.repeaterItemActions}>
                <Button
                  type="button"
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => handleRemove(index)}
                >
                  {removeLabel}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className={styles.repeaterAdd}>
        <Button type="button" variant="outlined" size="small" onClick={handleAdd}>
          {addLabel}
        </Button>
      </div>
    </div>
  );
}
